// The profile photo: preview URL, the pick → crop → upload path (file picker on
// the web, the native picker in the app), and the OS-permission popup flag.
// Split out of pages/ProfilePage.tsx; AvatarCropModal renders the cropper.
import React, { useCallback, useRef, useState } from "react";
import { Area } from "react-easy-crop";
import { Capacitor } from "@capacitor/core";
import { usersApi } from "../services/users";
import type { User } from "../types/User";
import { pickAvatarPhotoNative } from "../lib/avatarPhoto";
import { absMediaUrl } from "../utils/mediaUrl";
import { t } from "../i18n";
import { DEFAULT_AVATAR_SRC } from "../lib/defaultAvatar";

// Crop the source image to the user-selected area, scaling to fit `maxSize`
// and exporting at the chosen format/quality. JPEG + 1024px keeps avatar
// uploads well under the server's 2 MB cap.
async function cropToDataUrl(
  imageSrc: string,
  pixelCrop: Area,
  options?: { format?: "image/jpeg" | "image/png"; quality?: number; maxSize?: number }
): Promise<string> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = (err) => reject(err);
  });

  const format = options?.format ?? "image/jpeg";
  const quality = options?.quality ?? 0.85;
  const maxSize = options?.maxSize ?? 1024;
  const scale = Math.min(1, maxSize / Math.max(pixelCrop.width, pixelCrop.height));
  const outW = Math.max(1, Math.round(pixelCrop.width * scale));
  const outH = Math.max(1, Math.round(pixelCrop.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d")!;
  // JPEG has no alpha channel — fill white first so any transparency in the
  // source doesn't render as black after export.
  if (format === "image/jpeg") {
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, outW, outH);
  }
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outW,
    outH
  );
  return canvas.toDataURL(format, quality);
}

type Args = {
  user: User | null;
  setProfile: React.Dispatch<React.SetStateAction<User | null>>;
  initialAvatarUrl?: string | null;
};

export function useAvatarCropper({ user, setProfile, initialAvatarUrl = null }: Args) {
  // Seeded so the photo is there on the first frame (no pop-in when the fetch lands).
  const [avatarPreview, setAvatarPreview] = useState<string | null>(initialAvatarUrl);

  // Cropper state — when an avatar is picked (file picker or native camera),
  // we open the cropper modal with a circular overlay and hold the source
  // until the user confirms the crop.
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState<Area | null>(null);
  const [cropUploading, setCropUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // OS photo/camera permission denied → Settings deep-link popup.
  const [showPhotoAccessPopup, setShowPhotoAccessPopup] = useState(false);

  const uploadAvatarBlob = async (blob: Blob, filename = "avatar.jpg") => {
    if (!user) return;

    const tempUrl = URL.createObjectURL(blob);
    setAvatarPreview(tempUrl);

    const formData = new FormData();
    formData.append("avatar", blob, filename);

    try {
      const res = await usersApi.update(user.slug, formData);
      // Merge, don't replace: the PUT response carries only a subset of
      // fields, so replacing would drop venueName / earnings / toggles from
      // local state (the old "Update profile" full refetch used to mask this).
      setProfile((p) => (p ? { ...p, ...res } : (res as User)));
      // Use server URL if present; otherwise null (default avatar). Don't
      // fall back to tempUrl — we revoke it in finally and a revoked blob
      // URL renders broken, looking like "nothing happened" to the user.
      if (res?.avatarUrl) {
        setAvatarPreview(absMediaUrl(res.avatarUrl));
      } else {
        setAvatarPreview(null);
        throw new Error(t("useAvatarCropper.serverDidnTReturn"));
      }
    } catch (err: any) {
      console.error("Avatar upload failed:", err);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.msg;
      alert(
        serverMsg ||
        (status === 413
          ? t("useAvatarCropper.thatPhotoIsToo")
          : t("useAvatarCropper.couldnTUploadYour"))
      );
      try {
        const refreshed = await usersApi.get(user.slug);
        setProfile(refreshed);
        setAvatarPreview(absMediaUrl(refreshed.avatarUrl));
      } catch {}
    } finally {
      URL.revokeObjectURL(tempUrl);
    }
  };

  const openCropper = (src: string) => {
    // If a previous crop session left a blob URL around, revoke it before
    // we replace cropSrc with a new one (avoids small memory leaks).
    if (cropSrc && cropSrc.startsWith("blob:")) {
      URL.revokeObjectURL(cropSrc);
    }
    setCropSrc(src);
    setCropPosition({ x: 0, y: 0 });
    setCropZoom(1);
    setCropAreaPixels(null);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    // createObjectURL is instant; FileReader is a noticeable extra step on
    // mobile for big photos. Either kind of URL works as the Cropper image
    // source; we revoke the blob URL when the modal closes.
    const url = URL.createObjectURL(file);
    openCropper(url);
    e.target.value = "";
  };

  const handleAvatarClick = async () => {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) {
      fileInputRef.current?.click();
      return;
    }

    const result = await pickAvatarPhotoNative();
    if (result.status === "ok") {
      // Open the cropper instead of uploading directly so the user can frame
      // their photo into a circle (matches how the avatar is displayed).
      openCropper(result.dataUrl);
    } else if (result.status === "denied") {
      // OS-level photo/camera permission denied — the old silent no-op was a
      // punch-list item; the popup deep-links to Settings instead.
      setShowPhotoAccessPopup(true);
    } else if (result.status === "error") {
      window.alert(t("useAvatarCropper.couldnTOpenYour"));
    }
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCropAreaPixels(areaPixels);
  }, []);

  const cleanupCropBlobUrl = () => {
    if (cropSrc && cropSrc.startsWith("blob:")) {
      URL.revokeObjectURL(cropSrc);
    }
  };

  const cancelCrop = () => {
    if (cropUploading) return;
    cleanupCropBlobUrl();
    setCropSrc(null);
  };

  const confirmCrop = async () => {
    if (!cropSrc || !cropAreaPixels) return;
    setCropUploading(true);
    try {
      // JPEG @ 0.85 quality, max 1024px — keeps avatar uploads small enough
      // for the server's 2 MB cap regardless of source photo size.
      const dataUrl = await cropToDataUrl(cropSrc, cropAreaPixels, {
        format: "image/jpeg",
        quality: 0.85,
        maxSize: 1024,
      });
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      await uploadAvatarBlob(blob, "avatar.jpg");
      cleanupCropBlobUrl();
      setCropSrc(null);
    } catch (err) {
      console.error("Crop + upload failed:", err);
    } finally {
      setCropUploading(false);
    }
  };

  // (Remove-photo action retired with the drawer's photo row —
  //  DJs replace the photo by tapping it; PUT { avatarUrl: null } is the
  //  path if a remove control ever returns.)

  const defaultAvatarPath = DEFAULT_AVATAR_SRC;
  const avatarSrc = absMediaUrl(avatarPreview) ?? defaultAvatarPath;

  return {
    avatarPreview,
    setAvatarPreview,
    avatarSrc,
    defaultAvatarPath,
    cropSrc,
    cropPosition,
    setCropPosition,
    cropZoom,
    setCropZoom,
    cropAreaPixels,
    cropUploading,
    fileInputRef,
    showPhotoAccessPopup,
    setShowPhotoAccessPopup,
    handleAvatarChange,
    handleAvatarClick,
    onCropComplete,
    cancelCrop,
    confirmCrop,
  };
}

export type AvatarCropper = ReturnType<typeof useAvatarCropper>;
